import React, { useCallback, useMemo, useState } from 'react';
import { Control, Controller, FieldValues, UseControllerProps } from 'react-hook-form';
import cx from 'clsx';

import { CommonDependency } from '@/dependency';
import { useUserSearch, useDebounceState } from '@/hooks';

import { AntdSelect } from '../AntdSelect';
import style from './index.module.scss';

const { Option } = AntdSelect;

type Props = {
  name: string;
  className?: string;
  control: Control<FieldValues, object>;
  disabled?: boolean;
  inputWidth?: number;
  placeholder?: string;
  defaultUserInfo?: {
    desc?: UserDesc;
    id: string;
  };
  onChange?: (u?: UserInfo) => void;
  rules?: UseControllerProps['rules'];
};

export const UserSelectField = (props: Props) => {
  const UserCard = CommonDependency.components.UserCard;
  const {
    name,
    control,
    disabled,
    className,
    inputWidth,
    placeholder,
    defaultUserInfo,
    onChange,
    rules,
  } = props;

  const [value, setValue] = useState<string>();
  const [debouncedValue, setDebounceValue] = useDebounceState<string>(value);

  const { data: userList } = useUserSearch(debouncedValue);

  const handleSearch = useCallback(
    (v: string) => {
      setValue(v);
      setDebounceValue(v);
    },
    [setDebounceValue],
  );

  const shownUserList = useMemo(() => {
    return userList?.filter(user => String(user.id).startsWith('0x')).slice(0, 5) || [];
  }, [userList]);

  const defaultValue = useMemo(() => {
    return (
      defaultUserInfo && {
        key: defaultUserInfo.id,
        label: <UserCard userCardPopup={false} disableMemo data={defaultUserInfo?.desc} />,
      }
    );
  }, [defaultUserInfo, UserCard]);

  return (
    <Controller
      name={name}
      control={control}
      rules={
        rules || {
          required: true,
        }
      }
      render={({ field, fieldState }) => (
        <AntdSelect
          disabled={disabled}
          defaultValue={defaultValue}
          showSearch
          defaultActiveFirstOption={false}
          showArrow={false}
          filterOption={false}
          onSearch={handleSearch}
          onChange={(v: any) => {
            const selected = v && shownUserList?.find(x => v.key == x.id);
            field.onChange(selected);
            onChange?.(selected);
          }}
          notFoundContent={null}
          placeholder={placeholder}
          style={{ width: inputWidth }}
          className={cx(
            style.selector,
            className,
            disabled && style.isDisabled,
            !!fieldState.error && style.invalid,
          )}
          // allowClear
          labelInValue>
          {shownUserList?.map(d => (
            <Option key={d.id} value={d.id}>
              <UserCard userCardPopup={false} disableMemo data={d?.desc} />
            </Option>
          ))}
        </AntdSelect>
      )}
    />
  );
};
